import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Cookiepolicy() {
  useEffect(() => {
    document.title = "Cookiepolicy – vidalv.live";
    window.scrollTo(0, 0);
  }, []);

  const resetConsent = () => {
    localStorage.removeItem("vidalv_cookie_consent");
    window.location.reload();
  };

  return (
    <article data-testid="legal-cookiepolicy" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-pine hover:text-pine-hover no-underline mb-6">
        <ArrowLeft size={14} /> Tillbaka till startsidan
      </Link>
      <h1 className="font-fraktur-smooth text-falu text-3xl sm:text-4xl mb-2">Cookiepolicy</h1>
      <p className="text-xs text-bark/60 mb-8">Senast uppdaterad: 16 maj 2026</p>

      <section className="space-y-4 text-ink leading-relaxed">
        <p>
          Denna webbplats använder cookies i enlighet med Lagen om elektronisk kommunikation (LEK) och
          GDPR. En <em>cookie</em> är en liten textfil som sparas i din webbläsare.
          <strong> vidalv.live använder inga spårnings- eller reklamcookies.</strong>
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">1. Nödvändiga cookies (utan samtycke)</h2>
        <p>Dessa är tekniskt nödvändiga för att sajten ska fungera:</p>
        <div className="overflow-x-auto my-3">
          <table className="min-w-full text-sm border border-warm-border">
            <thead className="bg-bone-alt/60">
              <tr>
                <th className="text-left px-3 py-2 border-b border-warm-border">Namn</th>
                <th className="text-left px-3 py-2 border-b border-warm-border">Syfte</th>
                <th className="text-left px-3 py-2 border-b border-warm-border">Livslängd</th>
              </tr>
            </thead>
            <tbody className="bg-parchment">
              <tr><td className="px-3 py-2 border-b border-warm-border">mod_token</td><td className="px-3 py-2 border-b border-warm-border">Inloggning för moderatorer (sessionStorage)</td><td className="px-3 py-2 border-b border-warm-border">Till stängd flik</td></tr>
              <tr><td className="px-3 py-2 border-b border-warm-border">vidalv_cookie_consent</td><td className="px-3 py-2 border-b border-warm-border">Sparar att du sett integritetsnotisen</td><td className="px-3 py-2 border-b border-warm-border">12 månader</td></tr>
              <tr><td className="px-3 py-2 border-b border-warm-border">i18nextLng</td><td className="px-3 py-2 border-b border-warm-border">Språkval (sv/en/de m.fl.)</td><td className="px-3 py-2 border-b border-warm-border">12 månader</td></tr>
              <tr><td className="px-3 py-2">vidalv_accessibility_*</td><td className="px-3 py-2">Dina tillgänglighetsinställningar (textstorlek, kontrast)</td><td className="px-3 py-2">12 månader</td></tr>
            </tbody>
          </table>
        </div>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">2. Besöksstatistik utan cookies (server-hash-läge)</h2>
        <p>
          För att förstå hur sajten används kör vi <strong>PostHog</strong> i EU-molnet i ett{" "}
          <strong>cookielöst server-hash-läge</strong>. Ingen cookie sätts av PostHog, ingen data
          skrivs i din webbläsares localStorage eller sessionStorage, och ingen personprofil skapas.
          På serversidan hashar PostHog vissa tekniska signaler (bland annat IP + user-agent) till en
          kortlivad, icke-återvinningsbar identifierare enbart för att skilja unika besök från
          varandra under dygnet. Vi använder statistiken enbart för att se sidvisningar och
          teknisk prestanda (Web Vitals).
        </p>
        <div className="overflow-x-auto my-3">
          <table className="min-w-full text-sm border border-warm-border">
            <thead className="bg-bone-alt/60">
              <tr>
                <th className="text-left px-3 py-2 border-b border-warm-border">Tjänst</th>
                <th className="text-left px-3 py-2 border-b border-warm-border">Vad samlas in</th>
                <th className="text-left px-3 py-2 border-b border-warm-border">Leverantör</th>
              </tr>
            </thead>
            <tbody className="bg-parchment">
              <tr>
                <td className="px-3 py-2 border-b border-warm-border">PostHog (cookielöst)</td>
                <td className="px-3 py-2 border-b border-warm-border">Sidvisningar, referens, Web Vitals (LCP/CLS/INP m.fl.). IP + user-agent hashas serverside till ett dygnsvis rullande ID, ingen personprofil, ingen identify.</td>
                <td className="px-3 py-2 border-b border-warm-border">PostHog Inc. – EU Cloud (Frankfurt)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Eftersom PostHog körs cookielöst med server-hash-läge, utan personprofiler och utan
          identifiering, behandlar vi inte teknisk statistik som ett samtyckesbaserat inslag utan
          som en åtgärd som ryms i berättigat intresse enligt art. 6.1.f GDPR (aggregerad,
          pseudonymiserad statistik för webbdrift och prestanda). Vi delar inte dessa data med
          annonsnätverk. Vi använder <strong>inga</strong> marknadsförings- eller reklamcookies.
        </p>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">3. Hantera dina val</h2>
        <p>
          Du kan när som helst återställa integritetsnotisen:
        </p>
        <button
          onClick={resetConsent}
          data-testid="reset-cookie-consent"
          className="inline-flex items-center gap-2 bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors"
        >
          Visa integritetsnotisen igen
        </button>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">4. Blockera cookies i webbläsaren</h2>
        <p>Du kan också blockera eller radera cookies direkt i din webbläsare:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-falu">Chrome</a></li>
          <li><a href="https://support.mozilla.org/sv/kb/aktivera-och-inaktivera-kakor" target="_blank" rel="noopener noreferrer" className="text-falu">Firefox</a></li>
          <li><a href="https://support.apple.com/sv-se/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-falu">Safari</a></li>
          <li><a href="https://support.microsoft.com/sv-se/microsoft-edge" target="_blank" rel="noopener noreferrer" className="text-falu">Edge</a></li>
        </ul>

        <h2 className="font-heading text-xl font-semibold text-ink mt-6 mb-2">5. Kontakt</h2>
        <p>
          Frågor om cookies: <a href="mailto:redaktion@vidalv.live" className="text-falu">redaktion@vidalv.live</a>.
          Se även vår <Link to="/integritetspolicy" className="text-falu">integritetspolicy</Link>.
        </p>
      </section>
    </article>
  );
}
